/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 *
 ********************************************************************!*/

import { IChatbotResult } from "@/api/bot";
import { IPlan } from "@/store/types";
import {
  IInvoiceResult,
  IPaymentMethodResult,
  ISubscriptionResult,
} from "@chatcaptain/types/billing";
import { IUserResult, IGroupResult } from "@chatcaptain/types/organization";

export interface IAdminState {
  users: IUserResult[] | null;
  groups: IGroupResult[] | null;
  chatbots: IChatbotResult[] | null;
  subscription: ISubscriptionResult | null;
  permissionNames: IPermission;
  permissionsOrder: IPermissionsOrder;
  paymentMethods: IPaymentMethodResult[] | null;
  invoices: IInvoiceResult[] | null;
  plans: {
    startup: IPlan;
    business: IPlan;
    enterprise: IPlan;
  };
}
interface IPermission {
  [key: string]: string;
}
interface IPermissionsOrder {
  [key: string]: number;
}

export const state: IAdminState = {
  users: null,
  groups: null,
  chatbots: null,
  subscription: {} as ISubscriptionResult,
  permissionNames: {
    "cc.global.create": "Chatbots erstellen",
    "cc.global.edit": "Chatbots bearbeiten",
    "cc.global.delete": "Chatbots löschen",
    "cc.global.inbox": "Posteingang verwenden",
    "cc.roles.marketing": "Marketing Funktionen",
    "cc.chatbots.any": "Zugriff auf alle Chatbots",
  },
  permissionsOrder: {
    "cc.global.create": 1,
    "cc.global.edit": 2,
    "cc.global.delete": 3,
    "cc.global.inbox": 4,
    "cc.roles.marketing": 5,
    "cc.chatbots.any": 6,
  },
  paymentMethods: null,
  invoices: null,
  plans: {
    startup: {
      name: "Startup",
      description:
        "Alles was du brauchst um mit einem Chatbot zu starten und deine Kunden zu erreichen.",
      price: 69.95,
      interactions: 2500,
      features: [
        "Grafischer Chatbot Builder",
        "FAQ mit künstlicher Intelligenz",
        "Live Chat Funktion inkl. Posteingang mit Push-Benachrichtigungen",
        "Anpassbares Design & eigenes Logo",
        "Individuelle Zielgruppen",
        "3 Benutzer inklusive",
        "Support via E-Mail und Live Chat",
      ],
    },
    business: {
      name: "Business",
      description:
        "Für kleine Unternehmen, die mit einem Chatbot ihr Marketing oder den Support automatisieren möchten.",
      price: 489.95,
      interactions: 10000,
      features: [
        "Alles aus Startup plus:",
        "Hilfeartikel im Chatbot",
        "Einfache Analysen und Statistiken",
        "Chat Widget voll anpassbar via CSS",
        "Bedingungen und JSON API Abfragen",
        "Vorgefertigte und individuelle Entitäten",
        "Kein ChatCaptain Branding",
      ],
    },
    enterprise: {
      name: "Enterprise",
      description:
        "KMUs die mehr Power und verbesserte Analysen wünschen, für Datengetriebene Entscheidungen.",
      price: 1199.95,
      interactions: 25000,
      features: [
        "Alles aus Business plus:",
        "Programmierbare Elemente im Chatbot Builder",
        "Push-Benachrichtigungen",
        "Umfangreiche Analysen und Statistiken",
        "API Zugriff",
        "Onboarding durch unsere Chatbot-Experten",
      ],
    },
  },
};
